import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { toastify } from "@helpers/toast";
import { OPS_ENDPOINTS } from "@constants/endpoints";
import { toContractMeta } from "@panel/shared/contract/misc/helpers";
import { TRIPARTITE_COPY } from "../misc/constants";
import { normalizeTemplate } from "../misc/helpers";

const payloadOf = (res) => res?.data ?? res;

/**
 * The brand's contract template: the one PDF ops uploads, which every
 * publisher's contract is generated from when it is sent.
 * @param brandId
 */
const useBrandTemplate = (brandId) => {
    const { axiosInstance, loading } = useAxios({ base: "ops", initialLoader: true, showAlert: false });
    const { axiosInstance: saveInstance, loading: saving } = useAxios({ base: "ops", showAlert: false });

    const [template, setTemplate] = useState(null);

    const fetchTemplate = async () => {
        if (!brandId) return;

        const data = await axiosInstance
            .get(OPS_ENDPOINTS.BRAND_CONTRACT(brandId), { params: { limit: 1 } })
            .then(payloadOf)
            .catch(() => null) || {};

        setTemplate(normalizeTemplate(data.template));
    };

    useEffect(() => {
        setTemplate(null);
        fetchTemplate();
    }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * Saves the uploaded file as the brand's contract. The file is already in
     * storage by this point — this is the confirmation step that turns it into
     * the contract. 409 while publishers are still signing the current version.
     * @param staged the uploader's response
     */
    const saveTemplate = async (staged) => {
        if (!brandId || !staged?.url) return false;

        try {
            await saveInstance.post(OPS_ENDPOINTS.BRAND_CONTRACT(brandId), {
                contract_url : staged.url,
                contract_meta: toContractMeta(staged)
            });

            await fetchTemplate();
            toastify({ title: TRIPARTITE_COPY.saved, type: "success" });
            return true;
        } catch (error) {
            toastify({ title: error?.response?.data?.message || TRIPARTITE_COPY.saveFailure, type: "error" });
            return false;
        }
    };

    return { loading, saving, template, saveTemplate };
};

export default useBrandTemplate;
